import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { SearchSelect, Select, type SearchOption } from '@voice/ui';
import { ApiError, api } from '../lib/api';
import type { Destination, DirectoryResponse } from '../lib/types';

export function errorText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Etwas ist schiefgelaufen.';
}

export function useDirectory() {
  return useQuery({ queryKey: ['directory'], queryFn: () => api<DirectoryResponse>('/directory'), staleTime: 5_000, refetchOnMount: 'always' });
}

export const DESTINATION_LABELS: Record<Destination['type'], string> = {
  extension: 'Nebenstelle',
  flow: 'Ablauf / Sekretariat',
  queue: 'Warteschlange',
  voicemail: 'Anrufbeantworter',
  queuemail: 'Anrufbeantworter einer Warteschlange',
  ringgroup: 'Ringgruppe',
  conference: 'Konferenzraum',
  hangup: 'Besetzt / auflegen',
};

type Allowed = Destination['type'];

/** Auswahl eines Ziels: erst die Art, dann das konkrete Ziel. */
export function DestinationPicker({ label, value, onChange, allow = ['extension', 'flow', 'queue', 'voicemail', 'hangup'] }: { label: string; value: Destination; onChange: (d: Destination) => void; allow?: Allowed[] }) {
  const dir = useDirectory();
  const d = dir.data;
  // Ein leeres Ziel ist ungültig: sobald die Liste da ist, wird der erste Eintrag vorgewählt.
  const first = value.type !== 'hangup' && value.ref === '' ? targetOptions(value.type, d)[0]?.value : undefined;
  useEffect(() => {
    if (first && value.type !== 'hangup') onChange({ type: value.type, ref: first } as Destination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [first]);
  const setType = (type: Allowed) => {
    if (type === 'hangup') return onChange({ type });
    const first =
      type === 'extension' || type === 'voicemail' ? d?.extensions[0]?.number : type === 'flow' ? d?.flows[0]?.id : type === 'queue' || type === 'queuemail' ? d?.queues[0]?.id : type === 'conference' ? d?.conferences[0]?.id : '';
    onChange({ type, ref: first ?? '' } as Destination);
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: value.type === 'hangup' ? '1fr' : '1fr 1fr', gap: 12 }}>
      <Select label={label} value={value.type} onChange={(e) => setType(e.target.value as Allowed)}>
        {allow.map((t) => (
          <option key={t} value={t}>
            {DESTINATION_LABELS[t]}
          </option>
        ))}
      </Select>
      {value.type !== 'hangup' && (
        <SearchSelect label="Ziel" value={value.ref} onChange={(ref) => onChange({ type: value.type, ref } as Destination)} options={targetOptions(value.type, d)} placeholder="Suchen …" />
      )}
    </div>
  );
}

export function targetOptions(type: Destination['type'], d: DirectoryResponse | undefined): SearchOption[] {
  if (!d) return [];
  switch (type) {
    case 'extension':
    case 'voicemail':
      return d.extensions.map((x) => ({ value: x.number, label: x.name, hint: x.number }));
    case 'flow':
      return d.flows.map((x) => ({ value: x.id, label: x.name, hint: x.number ?? '' }));
    case 'queue':
    case 'queuemail':
      return d.queues.map((x) => ({ value: x.id, label: x.name, hint: x.number ?? '' }));
    case 'conference':
      return d.conferences.map((x) => ({ value: x.id, label: x.name, hint: x.number }));
    default:
      return [];
  }
}

export function describeDestination(d: Destination, dir: DirectoryResponse | undefined): string {
  if (d.type === 'hangup') return 'Besetzt';
  const name =
    d.type === 'extension' || d.type === 'voicemail'
      ? dir?.extensions.find((x) => x.number === d.ref)?.name
      : d.type === 'flow'
        ? dir?.flows.find((x) => x.id === d.ref)?.name
        : d.type === 'queue' || d.type === 'queuemail'
          ? dir?.queues.find((x) => x.id === d.ref)?.name
          : dir?.conferences.find((x) => x.id === d.ref)?.name;
  const ref = d.type === 'extension' || d.type === 'voicemail' ? d.ref : '';
  return `${DESTINATION_LABELS[d.type]}: ${[ref, name].filter(Boolean).join(' – ') || '…'}`;
}

export function formatDuration(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

export function formatDateTime(iso: string | number | Date): string {
  return new Date(iso).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
}
